import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCreditNotesApi,
  getCreditNoteApi,
  createCreditNoteApi,
  updateCreditNoteApi,
  deleteCreditNoteApi,
} from "../api/creditNotesApi";
import type { StoreId, CreditNoteId } from "@KassaroAB/shared/types/ids";
import type { RestCreateCreditNote, RestUpdateCreditNote } from "@KassaroAB/shared/types/creditNote";

export const useGetCreditNotesQuery = (storeId: StoreId | null) => {
  return useQuery({
    queryKey: ["stores", storeId, "creditNotes"],
    queryFn: () => getCreditNotesApi(storeId!),
    enabled: !!storeId,
  });
};

export const useGetCreditNoteQuery = (
  storeId: StoreId | null,
  creditNoteId: CreditNoteId | null,
) => {
  return useQuery({
    queryKey: ["stores", storeId, "creditNotes", creditNoteId],
    queryFn: () => getCreditNoteApi(storeId!, creditNoteId!),
    enabled: !!storeId && !!creditNoteId,
  });
};

export const useCreateCreditNoteMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (creditNote: RestCreateCreditNote) =>
      createCreditNoteApi(storeId!, creditNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "creditNotes"] });
    },
  });
};

export const useUpdateCreditNoteMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      creditNoteId,
      creditNote,
    }: {
      creditNoteId: CreditNoteId;
      creditNote: RestUpdateCreditNote;
    }) => updateCreditNoteApi(storeId!, creditNoteId, creditNote),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "creditNotes"] });
    },
  });
};

export const useDeleteCreditNoteMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (creditNoteId: CreditNoteId) =>
      deleteCreditNoteApi(storeId!, creditNoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "creditNotes"] });
    },
  });
};
